// src/AuthProvider.js
import React, { createContext, useEffect, useMemo, useState } from "react";
import keycloak from "./keycloak";

export const AuthContext = createContext({
  keycloak: null,
  authenticated: false,
  userRoles: [],
  userInfo: {},
  kcReady: false,
  login: () => {},
  logout: () => {},
});

const AuthProvider = ({ children }) => {
  const [kcReady, setKcReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [userInfo, setUserInfo] = useState({});

  useEffect(() => {
    let active = true;
    let refreshTimer = null;

    const readRoles = (parsed) => {
      const roles = new Set();

      // realm roles
      (parsed?.realm_access?.roles || []).forEach((r) => {
        if (r) roles.add(String(r).toLowerCase());
      });

      // client roles
      const clientId =
        keycloak?.clientId ||
        keycloak?.config?.clientId || // <-- keycloak-js zet het hier
        "lumen";

      (parsed?.resource_access?.[clientId]?.roles || []).forEach((r) => {
        if (r) roles.add(String(r).toLowerCase());
      });

      return Array.from(roles);
    };

    const setFromTokens = () => {
      const parsed = keycloak?.tokenParsed || {};
      setUserInfo(parsed);
      setUserRoles(readRoles(parsed));
      setAuthenticated(!!keycloak?.authenticated);
    };

    (async () => {
      try {
        // 1) init — geef SSO kans zonder redirect
        const ok = await keycloak.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri:
            window.location.origin + "/silent-check-sso.html",
          pkceMethod: "S256",
        });
        if (!active) return;

        // 2) auth-state vastleggen
        if (ok) {
          setFromTokens();
          // 3) korte first refresh
          try {
            await keycloak.updateToken(30);
            if (!active) return;
            setFromTokens();
          } catch {
            /* ignore first refresh */
          }

          // 4) auto-refresh elke 20s (houd token ≥30s geldig)
          refreshTimer = setInterval(async () => {
            try {
              // Avoid re-rendering the whole app if the token did not change.
              const prevToken = keycloak?.token;
              const refreshed = await keycloak.updateToken(30);
              if (!active) return;
              if (refreshed || keycloak?.token !== prevToken) {
                setFromTokens();
              }
            } catch {
              // ignore: UI can recover via normal auth flow
            }
          }, 20000);
        } else {
          // niet ingelogd
          setAuthenticated(false);
          setUserInfo({});
          setUserRoles([]);
        }
      } catch (e) {
        // init faalde (bv. silent-check niet bereikbaar)
        console.error("[AuthProvider] Keycloak init failed:", e?.message || e);
        setAuthenticated(false);
        setUserInfo({});
        setUserRoles([]);
      } finally {
        if (active) setKcReady(true);
      }
    })();

    // events (optioneel, maar handig)
    const onAuthSuccess = () => setFromTokens();
    const onAuthLogout = () => {
      setAuthenticated(false);
      setUserInfo({});
      setUserRoles([]);
    };
    keycloak.onAuthSuccess = onAuthSuccess;
    keycloak.onAuthLogout = onAuthLogout;
    keycloak.onTokenExpired = async () => {
      try {
        await keycloak.updateToken(30);
        if (active) setFromTokens();
      } catch {
        /* ignore */
      }
    };

    return () => {
      active = false;
      if (refreshTimer) clearInterval(refreshTimer);
      // opruimen van handlers (niet verplicht maar netjes)
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthLogout = undefined;
      keycloak.onTokenExpired = undefined;
    };
  }, []);

  const login = (opts = {}) =>
    keycloak?.login?.({ redirectUri: window.location.href, ...opts });
  const logout = (opts = {}) =>
    keycloak?.logout?.({ redirectUri: window.location.origin, ...opts });

  const value = useMemo(
    () => ({
      keycloak,
      authenticated,
      userRoles,
      userInfo,
      kcReady,
      login,
      logout,
    }),
    [authenticated, userRoles, userInfo, kcReady]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
